import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { 
  Bitcoin,
  CheckCircle,
  Clock,
  XCircle,
  ArrowLeft,
  QrCode,
  Smartphone
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import './POSPayment.css';

const POSPayment = () => {
  const { invoiceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [invoiceData, setInvoiceData] = useState(location.state?.invoiceData || null);
  const [cart, setCart] = useState(location.state?.cart || []);
  const [total, setTotal] = useState(location.state?.total || 0);
  const [mode, setMode] = useState('customer'); // customer or merchant

  useEffect(() => {
    if (!invoiceData) {
      navigate('/pos');
      return;
    }

    // Poll for payment status
    const interval = setInterval(() => {
      checkPaymentStatus();
    }, 3000);

    return () => clearInterval(interval);
  }, [invoiceId]);

  const checkPaymentStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/payments/status/${invoiceId}`, { headers });
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('completed');
      } else if (response.data.payment_status === 'expired') {
        setPaymentStatus('expired');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const handleNewSale = () => {
    navigate('/pos');
  };

  if (paymentStatus === 'completed') {
    return (
      <div className="pos-payment-page success">
        <div className="payment-result">
          <div className="result-icon success">
            <CheckCircle size={64} />
          </div>
          <h1>Payment Successful!</h1>
          <div className="result-amount">${total.toFixed(2)}</div>
          <p>Transaction completed</p>
          
          <div className="cart-summary">
            <h3>Items Purchased:</h3>
            {cart.map((item, index) => (
              <div key={`${item.product_id || item.product_name}-${index}`} className="summary-item">
                <span>{item.quantity}x {item.product_name}</span>
                <span>${item.total.toFixed(2)}</span>
              </div>
            ))}
          </div>

          <button className="btn btn-primary btn-large" onClick={handleNewSale}>
            New Sale
          </button>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'expired') {
    return (
      <div className="pos-payment-page expired">
        <div className="payment-result">
          <div className="result-icon expired">
            <XCircle size={64} />
          </div>
          <h1>Payment Expired</h1>
          <p>Please start a new transaction</p>
          
          <button className="btn btn-primary btn-large" onClick={handleNewSale}>
            Back to POS
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pos-payment-page">
      <div className="payment-header">
        <button className="back-btn" onClick={() => navigate('/pos')}>
          <ArrowLeft size={20} />
        </button>
        <h2>Payment in Progress</h2>
        <div className="mode-toggle">
          <button 
            className={mode === 'merchant' ? 'active' : ''}
            onClick={() => setMode('merchant')}
          >
            Merchant View
          </button>
          <button 
            className={mode === 'customer' ? 'active' : ''}
            onClick={() => setMode('customer')}
          >
            Customer View
          </button>
        </div>
      </div>

      <div className={`payment-content ${mode}`}>
        {mode === 'customer' ? (
          // Customer-facing display
          <div className="customer-display">
            <div className="brand-header">
              <Bitcoin size={48} />
              <h1>Crypto Cashier</h1>
            </div>

            <div className="payment-amount">
              <span className="amount-label">Total Amount</span>
              <span className="amount-value">${total.toFixed(2)}</span>
            </div>

            <div className="qr-code-container">
              <QRCodeSVG 
                value={invoiceData.checkout_link}
                size={300}
                level="M"
                includeMargin={true}
              />
            </div>

            <div className="payment-instructions">
              <div className="instruction-step">
                <Smartphone size={24} />
                <p>Open your crypto wallet app</p>
              </div>
              <div className="instruction-step">
                <QrCode size={24} />
                <p>Scan QR code to pay</p>
              </div>
            </div>

            <div className="waiting-indicator">
              <Clock size={20} className="pulse" />
              <span>Waiting for payment...</span>
            </div>
          </div>
        ) : (
          // Merchant view
          <div className="merchant-display">
            <div className="payment-details-card">
              <h3>Payment Details</h3>
              
              <div className="detail-row">
                <span>Total Amount:</span>
                <span className="detail-value">${total.toFixed(2)}</span>
              </div>

              <div className="detail-row">
                <span>Status:</span>
                <span className="status-badge pending">
                  <Clock size={16} />
                  Waiting for Payment
                </span>
              </div>

              <div className="detail-row">
                <span>Invoice ID:</span>
                <span className="detail-value small">{invoiceId}</span>
              </div>
            </div>

            <div className="cart-details-card">
              <h3>Cart Items ({cart.length})</h3>
              {cart.map((item, index) => (
                <div key={`${item.product_id || item.product_name}-${index}`} className="cart-detail-item">
                  <div>
                    <div className="item-name">{item.product_name}</div>
                    <div className="item-meta">{item.quantity} × ${item.price.toFixed(2)}</div>
                  </div>
                  <div className="item-total">${item.total.toFixed(2)}</div>
                </div>
              ))}
              
              <div className="cart-total">
                <span>Total:</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>

            <div className="qr-preview">
              <p>Customer scans this QR code:</p>
              <QRCodeSVG 
                value={invoiceData.checkout_link}
                size={200}
                level="M"
              />
            </div>

            <button className="btn btn-secondary" onClick={() => navigate('/pos')}>
              Cancel & Return
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default POSPayment;
